/**
 * Alpine.js Language Editor Component
 * Provides inline editing functionality for user languages using Alpine.js
 */

import { getApiToken } from './tokenUtils.js';

// Language data - should ideally be imported from a central source
const availableLanguages = {
    'AB': 'Abkhazian', 'AA': 'Afar', 'AF': 'Afrikaans', 'AK': 'Akan', 'SQ': 'Albanian',
    'AM': 'Amharic', 'AR': 'Arabic', 'AC': 'Aramaic', 'HY': 'Armenian', 'AS': 'Assamese', 'AW': 'Awadhi',
    'AY': 'Aymara', 'AZ': 'Azerbaijani', 'BL': 'Balochi', 'BA': 'Bashkir', 'EU': 'Basque',
    'BE': 'Belarussian', 'BN': 'Bengali', 'B1': 'Bhojpuri', 'DZ': 'Bhutani', 'BH': 'Bihari',
    'BI': 'Bislama', 'BR': 'Breton', 'BG': 'Bulgarian', 'MY': 'Burmese', 'YU': 'Cantonese', 'CA': 'Catalan',
    'CB': 'Cebuano', 'CE': 'Chechen', 'ZH': 'Chinese', 'CO': 'Corsican', 'HR': 'Croatian',
    'CS': 'Czech', 'DK': 'Dakhini', 'DA': 'Danish', 'NL': 'Dutch', 'EN': 'English',
    'EO': 'Esperanto', 'ET': 'Estonian', 'FO': 'Faeroese', 'FJ': 'Fiji', 'FI': 'Finnish',
    'FR': 'French', 'FY': 'Frisian', 'GL': 'Galician', 'KA': 'Georgian', 'DE': 'German',
    'EL': 'Greek', 'KL': 'Greenlandic', 'GN': 'Guarani', 'GU': 'Gujarati', 'HT': 'Haitian Creole',
    'HA': 'Hausa', 'IW': 'Hebrew', 'HI': 'Hindi', 'HK': 'Hokkien', 'HU': 'Hungarian', 'IS': 'Icelandic',
    'IO': 'Ido', 'ID': 'Indonesian', 'IH': 'Ingush', 'IA': 'Interlingua', 'IE': 'Interlingue', 'IU': 'Inuktitut',
    'GA': 'Irish', 'IT': 'Italian', 'JA': 'Japanese', 'JV': 'Javanese', 'KN': 'Kannada',
    'KC': 'Karachay-Balkar', 'K1': 'Karelian',
    'KS': 'Kashmiri', 'KK': 'Kazakh', 'KM': 'Khmer', 'KO': 'Korean', 'KU': 'Kurdish',
    'KY': 'Kyrgyz', 'LO': 'Lao', 'LA': 'Latin', 'LV': 'Latvian', 'LT': 'Lithuanian',
    'MK': 'Macedonian', 'MG': 'Malagasy', 'MS': 'Malay', 'ML': 'Malayalam', 'MT': 'Maltese',
    'MI': 'Maori', 'MR': 'Marathi', 'MO': 'Moldavian', 'MN': 'Mongolian', 'NH': 'Nahuatl', 'NA': 'Nauru',
    'ND': 'North Ndebele', 'NP': 'Neapolitan', 'NE': 'Nepali', 'NO': 'Norwegian', 'OC': 'Occitan', 'OR': 'Oriya',
    'OS': 'Ossetian', 'PS': 'Pashto',
    'FA': 'Persian', 'PL': 'Polish', 'PT': 'Portuguese', 'PA': 'Punjabi', 'QU': 'Quechua',
    'RM': 'Rhaeto-Romance', 'RO': 'Romanian', 'RU': 'Russian', 'SM': 'Samoan', 'SG': 'Sangro',
    'SA': 'Sanskrit', 'S2': 'Scots', 'GD': 'Scottish Gaelic', 'SR': 'Serbian', 'SH': 'Serbo-Croatian', 'ST': 'Sesotho',
    'TN': 'Setswana', 'SN': 'Shona', 'SX': 'Sicilian', 'NR': 'South Ndebele', 'SD': 'Sindhi', 'SI': 'Sinhalese', 'SS': 'Siswati',
    'SK': 'Slovak', 'SL': 'Slovenian', 'SO': 'Somali', 'ES': 'Spanish', 'SU': 'Sundanese',
    'SW': 'Swahili', 'SV': 'Swedish', 'TL': 'Tagalog', 'TG': 'Tajik', 'TA': 'Tamil',
    'TT': 'Tatar', 'TE': 'Telugu', 'TH': 'Thai', 'BO': 'Tibetan', 'TI': 'Tigrinya',
    'TO': 'Tonga', 'TS': 'Tsonga', 'TR': 'Turkish', 'TK': 'Turkmen', 'TW': 'Twi',
    'UD': 'Udmurt', 'UK': 'Ukrainian', 'UR': 'Urdu', 'UZ': 'Uzbek', 'VI': 'Vietnamese', 'VO': 'Volapuk',
    'CY': 'Welsh', 'WO': 'Wolof', 'XH': 'Xhosa', 'YI': 'Yiddish', 'YO': 'Yoruba', 'ZU': 'Zulu'
};

const popularLanguages = [
    'EN', 'ES', 'FR', 'DE', 'IT', 'PT', 'RU', 'ZH', 'JA', 'KO', 
    'AR', 'HI', 'NL', 'SV', 'NO', 'DA', 'FI', 'PL', 'TR', 'EL'
];

// Alpine.js Language Editor Component Factory
function languageEditor(initialLanguages = [], type = 'known') {
    return {
        // State
        type: type, // 'known' or 'learning'
        languages: [...initialLanguages],
        isEditing: false,
        saving: false,
        saveMessage: '',
        saveError: false,
        showAll: false, // FIX: Initialize property
        
        // Computed properties with performance optimizations
        get filteredLanguages() {
            // Cache expensive operations
            if (!this._existingCodesCache || this._lastLanguagesLength !== this.languages.length) {
                this._existingCodesCache = new Set(this.languages.map(l => l.lang));
                this._lastLanguagesLength = this.languages.length;
            }
            
            const term = this.searchTerm?.toLowerCase() || '';
            // Fallback to local data if store not available yet
            const availableLanguageData = (this.$store?.languageData?.available) || availableLanguages;
            
            return Object.entries(availableLanguageData)
                .filter(([code, name]) => 
                    !this._existingCodesCache.has(code) && 
                    (name.toLowerCase().includes(term) || code.toLowerCase().includes(term))
                )
                .sort(([, nameA], [, nameB]) => nameA.localeCompare(nameB));
        },
        
        get popularAvailableLanguages() {
            if (!this._existingCodesCache || this._lastLanguagesLength !== this.languages.length) {
                this._existingCodesCache = new Set(this.languages.map(l => l.lang));
                this._lastLanguagesLength = this.languages.length;
            }
            
            const term = this.searchTerm?.toLowerCase() || '';
            // Fallback to local data if store not available yet
            const availableLanguageData = (this.$store?.languageData?.available) || availableLanguages;
            const popularLanguageData = (this.$store?.languageData?.popular) || popularLanguages;
            
            return popularLanguageData
                .filter(code => availableLanguageData[code] && !this._existingCodesCache.has(code))
                .filter(code => {
                    const name = availableLanguageData[code];
                    return name.toLowerCase().includes(term) || code.toLowerCase().includes(term);
                })
                .sort((a, b) => availableLanguageData[a].localeCompare(availableLanguageData[b]));
        },
        
        get visibleLanguages() {
            return this.languages.slice(0, 5);
        },
        
        get hiddenLanguages() {
            return this.languages.slice(5);
        },
        
        get hasHiddenLanguages() {
            return this.languages.length > 5;
        },
        
        get showingAll() {
            return this.showAll;
        },
        
        // Methods with proper lifecycle management
        init() {
            // Initialize cache properties
            this._existingCodesCache = null;
            this._lastLanguagesLength = 0;
            this._timeoutIds = new Set();
            
            // Set up cleanup when component is destroyed - only if $cleanup is available (Alpine 3.x)
            if (typeof this.$cleanup === 'function') {
                this.$cleanup(() => {
                    this._timeoutIds.forEach(id => clearTimeout(id));
                    this._timeoutIds.clear();
                });
            }
        },
        
        startEdit() {
            this.isEditing = true;
        },
        
        completeEdit() {
            this.isEditing = false;
            this.showAll = false;
        },
        
        openModal() {
            // Use Alpine's event system for proper component communication
            this.$dispatch('language-modal-open', {
                targetEditor: this,
                type: this.type
            });
        },
        
        addLanguage(language) {
            // Called by the modal when a language is selected
            this.languages.push(language);
            this.autoSave();
        },
        
        removeLanguage(index) {
            this.languages.splice(index, 1);
            this.autoSave();
        },
        
        toggleShowAll() {
            this.showAll = !this.showAll;
        },
        
        getLevelInfo(level) {
            const levels = {
                0: { name: 'Beginner', icon: 'fa-seedling', color: 'gray' },
                1: { name: 'Intermediate', icon: 'fa-graduation-cap', color: 'amber' },
                2: { name: 'Advanced', icon: 'fa-rocket', color: 'amber' },
                3: { name: 'Proficient', icon: 'fa-star', color: 'amber' },
                4: { name: 'Native Speaker', icon: 'fa-crown', color: 'emerald' }
            };
            return levels[level] || levels[0];
        },
        
        getChipStyles(level) {
            // Colors: levels 0-3 = amber, level 4 (native) = green
            const styles = {
                0: { bg: '#fef3c7', border: '#fde68a', text: '#92400e', dotColor: 'bg-amber-500' },
                1: { bg: '#fef3c7', border: '#fde68a', text: '#92400e', dotColor: 'bg-amber-500' },
                2: { bg: '#fef3c7', border: '#fde68a', text: '#92400e', dotColor: 'bg-amber-500' },
                3: { bg: '#fef3c7', border: '#fde68a', text: '#92400e', dotColor: 'bg-amber-500' },
                4: { bg: '#ecfdf5', border: '#a7f3d0', text: '#065f46', dotColor: 'bg-emerald-500' }
            };
            return styles[level] || styles[0];
        },
        
        async autoSave() {
            this.saving = true;
            this.saveError = false;
            
            try {
                const apiToken = await getApiToken();
                if (!apiToken) {
                    throw new Error('No API token found');
                }
                
                // Prepare API request
                const endpoint = '/v1/user-languages/self';
                const body = new FormData();
                
                const languageData = this.languages.map(lang => ({
                    lang: lang.lang.toUpperCase(),
                    level: parseInt(lang.level)
                }));
                
                body.append(this.type === 'known' ? 'langs_known' : 'langs_learning', JSON.stringify(languageData));
                
                // Get CSRF token
                const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
                
                const response = await fetch(endpoint, {
                    method: 'PUT',
                    body: body,
                    headers: {
                        'X-AUTH-TOKEN': apiToken,
                        'X-CSRF-TOKEN': csrfToken || ''
                    },
                    credentials: 'include'
                });
                
                if (!response.ok) {
                    const error = await response.json();
                    throw new Error(error.message || 'Failed to save languages');
                }
                
                // Success
                this.saveMessage = 'Saved';
                this.saveError = false;
                
                const timeoutId = setTimeout(() => {
                    this.saveMessage = '';
                }, 2000);
                this._timeoutIds.add(timeoutId);
                setTimeout(() => this._timeoutIds.delete(timeoutId), 2000);
                
            } catch (error) {
                console.error('Error saving languages:', error);
                this.saveMessage = 'Failed to save';
                this.saveError = true;
                
                const timeoutId = setTimeout(() => {
                    this.saveMessage = '';
                    this.saveError = false;
                }, 3000);
                this._timeoutIds.add(timeoutId);
                setTimeout(() => this._timeoutIds.delete(timeoutId), 3000);
                
            } finally {
                this.saving = false;
            }
        }
    };
}

// Export the language data and component factory
export { availableLanguages, popularLanguages, languageEditor };
